import { Card, CardContent } from '../../../components/ui/card';
import { FileSpreadsheet, Clock, MapPin, Euro, Package, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { formatHours } from '../utils/calculations';
import { Money } from '../../../components/Money';

// Üst özet kartları: toplam + rota / gün / şoför başına ortalamalar + önceki dönem trendi.
function DeltaBadge({ current, previous, format, isMoney = false }) {
  if (previous === null || previous === undefined) return null;
  if (!previous && !current) return null;
  const diff = current - previous;
  const pct = previous === 0 ? (current > 0 ? 100 : 0) : ((diff / Math.abs(previous)) * 100);
  const absPct = Math.abs(pct);
  const display = absPct < 0.1 ? '0' : (absPct >= 100 ? Math.round(absPct) : absPct.toFixed(1));
  const sign = diff === 0 ? '=' : (diff > 0 ? '+' : '−');
  let color = 'text-slate-400';
  let Icon = Minus;
  if (diff > 0) { color = 'text-emerald-600 dark:text-emerald-400'; Icon = TrendingUp; }
  else if (diff < 0) { color = 'text-rose-600 dark:text-rose-400'; Icon = TrendingDown; }
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${color}`}>
      <Icon className="h-3 w-3" />
      {sign}{display}%
      {isMoney ? (
        <span className="opacity-60 font-normal ml-0.5">(<Money amount={previous} />)</span>
      ) : (format && <span className="opacity-60 font-normal ml-0.5">({format(previous)})</span>)}
    </span>
  );
}

const num1 = (v) => (v || 0).toLocaleString('tr-TR', { maximumFractionDigits: 1 });
const div = (a, b) => (b > 0 ? a / b : 0);

function AvgLines({ items, testId }) {
  return (
    <div className="mt-1 space-y-0.5" data-testid={testId}>
      {items.map(({ label, value }) => (
        <p key={label} className="text-[11px] text-slate-400 dark:text-slate-500 leading-tight">
          ⌀ <span className="text-slate-600 dark:text-slate-300 font-medium">{value}</span> <span className="opacity-70">/ {label}</span>
        </p>
      ))}
    </div>
  );
}

function SummaryCard({ title, value, icon: Icon, iconClass, avgs, avgTestId, trend, footnote, valueClass = 'text-slate-800 dark:text-slate-100', testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm text-slate-500 dark:text-slate-400">{title}</p>
            <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
            {avgs && <AvgLines items={avgs} testId={avgTestId} />}
            {footnote && <p className="text-[10px] text-slate-400 mt-0.5">{footnote}</p>}
            {trend && <div className="mt-1 flex items-center gap-1 flex-wrap">{trend}</div>}
          </div>
          <Icon className={`h-8 w-8 shrink-0 ${iconClass}`} />
        </div>
      </CardContent>
    </Card>
  );
}

export function RoutesSummary({ summary, previousSummary, showKm = true, t }) {
  const { count = 0, dayCount = 0, driverCount = 0, totalHours, totalKm, totalCost, totalStops = 0, kmRoutes = 0, stopRoutes = 0 } = summary;
  const prev = previousSummary?.totals || null;
  const vsLabel = previousSummary?.label || 'önceki dönem';
  const showStops = totalStops > 0;
  const perSet = (total, fmt, routeBase = count) => [
    { label: 'rota', value: fmt(div(total, routeBase)) },
    { label: 'gün', value: fmt(div(total, dayCount)) },
    { label: 'şoför', value: fmt(div(total, driverCount)) },
  ];
  const money = (v) => <Money amount={v} />;
  const cols = 3 + (showKm ? 1 : 0) + (showStops ? 1 : 0);

  return (
    <div className={`grid grid-cols-2 md:grid-cols-3 gap-4 ${cols >= 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
      <SummaryCard
        testId="routes-summary-count"
        title={t('routes.summary.totalRecords') || 'Toplam Kayıt'}
        value={count}
        icon={FileSpreadsheet}
        iconClass="text-blue-500"
        avgs={[
          { label: 'gün', value: num1(div(count, dayCount)) },
          { label: 'şoför', value: num1(div(count, driverCount)) },
        ]}
        footnote={`${dayCount} gün · ${driverCount} şoför`}
        avgTestId="routes-summary-avg-count"
        trend={prev && (<><DeltaBadge current={count} previous={prev.route_count || 0} /><span className="text-[10px] text-slate-400">vs {vsLabel}</span></>)}
      />
      <SummaryCard
        testId="routes-summary-hours"
        title={t('routes.summary.totalHours') || 'Toplam Saat'}
        value={formatHours(totalHours)}
        icon={Clock}
        iconClass="text-green-500"
        avgs={perSet(totalHours, formatHours)}
        avgTestId="routes-summary-avg-hours"
        trend={prev && <DeltaBadge current={totalHours} previous={prev.total_hours || 0} format={formatHours} />}
      />
      {showKm && (
        <SummaryCard
          testId="routes-summary-km"
          title={t('routes.summary.totalKm') || 'Toplam KM'}
          value={(totalKm || 0).toLocaleString('tr-TR')}
          icon={MapPin}
          iconClass="text-orange-500"
          avgs={perSet(totalKm, num1, kmRoutes)}
          avgTestId="routes-summary-avg-km"
          trend={prev && <DeltaBadge current={totalKm} previous={prev.total_km || 0} format={(v) => num1(v)} />}
        />
      )}
      {showStops && (
        <SummaryCard
          testId="routes-summary-stops"
          title="Toplam Stop"
          value={totalStops.toLocaleString('tr-TR')}
          icon={Package}
          iconClass="text-violet-500"
          avgs={perSet(totalStops, num1, stopRoutes)}
          avgTestId="routes-summary-avg-stops"
        />
      )}
      <SummaryCard
        testId="routes-summary-cost"
        title={t('routes.summary.totalAmount') || 'Toplam Tutar'}
        value={<Money amount={totalCost} />}
        valueClass="text-emerald-600"
        icon={Euro}
        iconClass="text-emerald-500"
        avgs={perSet(totalCost, money)}
        avgTestId="routes-summary-avg-cost"
        trend={prev && <DeltaBadge current={totalCost} previous={prev.total_cost || 0} isMoney />}
      />
    </div>
  );
}
